import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

export type OfferCountdownSize = "sm" | "lg";

const forwardable = (prop: PropertyKey) => prop !== "urgent" && prop !== "size";

/**
 * A pill sized to sit directly under the status tag on a booking card, so the deadline
 * reads as part of the status rather than as another action.
 *
 * `lg` is for the last-minute offer page, where the pill is not an annotation on a list of
 * bookings but the single most important thing on the screen — the whole page exists because
 * this number is running out.
 */
export const OfferCountdownContainer = styled("div", {
  shouldForwardProp: forwardable,
})<{ urgent: boolean; size?: OfferCountdownSize }>(({ theme, urgent, size = "sm" }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.space._100,
  marginTop: theme.space._100,
  padding: size === "lg" ? `${theme.space._100} ${theme.space._300}` : `${theme.space._10} ${theme.space._200}`,
  borderRadius: "6.25rem",
  border: "1px solid",
  borderColor: urgent ? theme.colors["red.700"] : theme.colors["neutral.700"],
  backgroundColor: urgent ? theme.colors["red.950"] : theme.colors["neutral.900"],
  // The clock glyph inherits currentColor, so it is tinted from here rather than per-icon.
  color: urgent ? theme.colors["red.300"] : theme.colors["neutral.300"],
  whiteSpace: "nowrap",
  ...(size === "lg" && { alignSelf: "center" }),
}));

export const OfferCountdownLabel = styled(Typography, {
  shouldForwardProp: forwardable,
})<{ urgent: boolean; size?: OfferCountdownSize }>(({ theme, urgent, size = "sm" }) => ({
  fontFamily: theme.fonts.default,
  fontSize: size === "lg" ? theme.fontSizes.sm : theme.fontSizes.xs,
  fontWeight: theme.fontWeights.semibold,
  lineHeight: theme.lineHeights.base,
  color: urgent ? theme.colors["red.300"] : theme.colors["neutral.300"],
}));

/**
 * Tabular figures: without them the whole pill jitters sideways every second as the digits
 * change width.
 */
export const OfferCountdownValue = styled(Typography, {
  shouldForwardProp: forwardable,
})<{ urgent: boolean; size?: OfferCountdownSize }>(({ theme, urgent, size = "sm" }) => ({
  fontFamily: theme.fonts.default,
  fontSize: size === "lg" ? theme.fontSizes.md : theme.fontSizes.xs,
  fontWeight: theme.fontWeights.bold,
  lineHeight: theme.lineHeights.base,
  fontVariantNumeric: "tabular-nums",
  color: urgent ? theme.colors["red.200"] : theme.colors["neutral.50"],
}));
